import { useEffect, useMemo, useState } from "react";
import { Menu, MenuList } from "../../utils/constants";
import { PERM, requiredPermissionForPath } from "../../auth/permissions";
import { useMe } from "../../auth/MeContext";
import axiosInstance from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";

import { LogoutOutlined } from "@ant-design/icons";
import { PageContainer, ProLayout } from "@ant-design/pro-components";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Dropdown } from "antd";
import { Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "../../theme/ThemeContext";
import { translateMenuName } from "../../utils/menuLabel";
import { Link, useLocation, useNavigate } from "react-router-dom";
import defaultAvatar from "../../assets/default/default-user-avatar.png";
import logoIcon from "../../assets/default/nailsmap-white.svg";
import logoWordmark from "../../assets/default/nailsmap-wordmark-white.svg";
import Notice from "./Notice";
import LanguageSwitcher from "./LanguageSwitcher";
import AppBreadcrumb from "../breadcrumb/AppBreadcrumb";
import { BreadcrumbProvider } from "../breadcrumb/BreadcrumbContext";

const BRAND = "var(--app-sidebar)";

const SIDEBAR_STYLE = `
  .ant-pro .ant-pro-layout .ant-pro-sider {
    background: ${BRAND} !important;
    font-family: "CircularXXWeb";
  }
  .ant-layout-sider-children { background: ${BRAND} !important; height: 100vh; }

  .ant-pro-base-menu .ant-menu-item,
  .ant-pro-base-menu .ant-menu-submenu-title {
    margin: 2px 8px !important;
    width: calc(100% - 16px) !important;
    border-radius: 10px !important;
    color: #fff !important;
  }
  /* Raw CSS in a <style> tag: line comments are invalid here and silently drop
     every rule until the next "{". Only block comments are allowed below. */
  .ant-pro-sider .ant-menu-item,
  .ant-pro-sider .ant-menu-submenu,
  .ant-pro-sider .ant-menu-submenu-title,
  .ant-pro-sider .ant-menu-submenu-title .ant-menu-title-content,
  .ant-pro-sider .ant-pro-base-menu-item-text {
    color: #fff !important;
  }
  /* antd sets selected submenu titles to #ba2156 (invisible on the pink sider). */
  .ant-pro-sider .ant-menu-submenu-selected > .ant-menu-submenu-title,
  .ant-pro-sider .ant-menu-submenu-open > .ant-menu-submenu-title {
    color: #fff !important;
  }
  .ant-pro-base-menu-item-text, .ant-menu-title-content {
    font-size: 0.95rem;
    font-weight: 600;
    color: inherit !important;
  }
  .ant-pro-base-menu .ant-pro-base-menu-item-icon,
  .ant-menu-item .anticon {
    min-width: 18px !important;
    font-size: 18px !important;
  }

  .ant-menu-item:hover, .ant-menu-submenu-title:hover {
    background: rgba(255, 255, 255, 0.16) !important;
    color: #fff !important;
  }
  .ant-menu-item-selected {
    background: rgba(255, 255, 255, 0.28) !important;
    color: #fff !important;
  }
  .ant-menu-item-selected .ant-menu-title-content,
  .ant-menu-item-selected a { color: #fff !important; }
  .ant-menu-item a, .ant-menu-item a:hover { color: #fff !important; }

  .ant-menu-submenu-selected > .ant-menu-submenu-title { background: rgba(255, 255, 255, 0.16) !important; }
  .ant-menu-sub.ant-menu-inline { background: transparent !important; }
  .ant-menu-submenu-arrow { color: #fff !important; }

  /* P0-1: chip text is dark on its light #f5f5f5 bg; hidden in collapsed rail (identity moves to Dropdown). */
  .ant-pro .ant-pro-layout .ant-pro-sider-actions {
    overflow: hidden !important;
  }
  .ant-pro .ant-pro-layout .ant-pro-sider-logo { padding: 16px 12px !important; }
  .ant-pro .ant-pro-layout .ant-pro-sider-logo img { height: auto !important; width: auto !important; }
  .ant-pro .ant-pro-layout .ant-pro-sider-actions-avatar { min-width: 0 !important; }
  .ant-pro .ant-pro-layout .ant-pro-sider-actions-avatar > span {
    color: #fff !important;
    min-width: 0 !important;
    max-width: 100%;
    overflow: hidden !important;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ant-pro-sider-collapsed .ant-pro-sider-actions-avatar > span,
  .ant-layout-sider-collapsed .ant-pro-sider-actions-avatar > span {
    display: none !important;
  }
`;

const Sidebar = ({ children }: { children: React.ReactNode }) => {
  const [collapse, setCollapse] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isDark, toggleMode } = useThemeMode();
  const email = localStorage.getItem("email");
  const avatar = localStorage.getItem("avatar");
  
  const { me, can, loading: meLoading } = useMe();
  const role = me?.role?.name || localStorage.getItem("current_user_role");
  const [pendingCoupons, setPendingCoupons] = useState(0);

  useEffect(() => {
    if (!can(PERM.COUPON_APPROVE)) return;
    axiosInstance
      .get(pathAPI.couponPendingCount)
      .then((res) => setPendingCoupons(Number(res.data?.data) || 0))
      .catch(() => setPendingCoupons(0));
  }, [me, location.pathname]);

  // Menu is built strictly from the permissions returned by /admin/me.
  // If /admin/me is unavailable nothing restricted is shown (the server still enforces access).
  const permissionKey = me ? [me.role?.code, ...me.permissions].join("|") : "";

  const visibleMenus = useMemo<Menu[]>(() => {
    if (meLoading || !me) return [];
    const filterMenus = (items: Menu[]): Menu[] =>
      items.reduce<Menu[]>((acc, item) => {
        const needed = requiredPermissionForPath(item.path);
        if (needed && !can(needed)) return acc;
        const children = item.routes ? filterMenus(item.routes) : [];
        if (item.routes && item.routes.length > 0 && children.length === 0) return acc;
        const label = translateMenuName(t, item);
        const name =
          item.path === "/coupon" && pendingCoupons > 0
            ? `${label} (${pendingCoupons})`
            : label;
        acc.push({ ...item, name, routes: children });
        return acc;
      }, []);
    return filterMenus(MenuList);
  }, [permissionKey, meLoading, pendingCoupons, i18n.language, t]);

  return (
    <BreadcrumbProvider>
      <style>{SIDEBAR_STYLE}</style>
      <ProLayout
      iconPrefixes=""
      title={false}
      logo={
        collapse ? (
          <img src={logoIcon} alt="Nailsmap" style={{ width: 44, height: 44 }} />
        ) : (
          <img src={logoWordmark} alt="Nailsmap" style={{ width: 160, height: "auto" }} />
        )
      }
      key={`${permissionKey}|${i18n.language}`}
      route={{ path: "/", routes: visibleMenus }}
      colorPrimary="#ba2156"
      onCollapse={setCollapse}
      collapsed={collapse}
      menuItemRender={(item, dom) => (
        <Link to={item.path || "#"}>
          <div>{dom}</div>
        </Link>
      )}
      avatarProps={{
        src: avatar || defaultAvatar,
        size: "large",
        title: (
          <Box
            sx={{
              display: "block",
              maxWidth: 168,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              backgroundColor: "transparent",
              color: "#fff",
              padding: "4px 8px",
            }}
          >
            <div style={{ fontWeight: "bold" }}>{email || ""}</div>
            {role && (
              <span
                style={{
                  display: "inline-block",
                  marginTop: 4,
                  padding: "1px 8px",
                  borderRadius: 999,
                  background: "rgba(255, 255, 255, 0.25)",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {role.replace("_", " ")}
              </span>
            )}
          </Box>
        ),
        render: (_props, dom) => {
          const menuItems = [
            ...(email || role
              ? [
                  {
                    key: "identity",
                    disabled: true,
                    label: (
                      <div style={{ color: "var(--app-text)", maxWidth: 220 }}>
                        <div style={{ fontWeight: 600 }}>{email || ""}</div>
                        {role && (
                          <div style={{ fontSize: 11, textTransform: "capitalize", color: "var(--app-text-muted)" }}>
                            {role.replace("_", " ")}
                          </div>
                        )}
                      </div>
                    ),
                  },
                ]
              : []),
            {
              key: "logout",
              label: t("sidebar.logout"),
              icon: <LogoutOutlined />,
              onClick: () => {
                localStorage.clear();
                navigate("/login");
              },
            },
          ];

          const dividerStyle = {
            borderTop: "1px solid rgba(255, 255, 255, 0.35)",
            paddingTop: 12,
            marginTop: 8,
          };

          return (
            <div>
              <div
                style={{
                  ...dividerStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapse ? "center" : "space-between",
                  gap: 8,
                  color: "#fff",
                  padding: collapse ? "12px 0" : "12px 8px",
                  marginBottom: 8,
                }}
              >
                {!collapse && (
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {t("sidebar.notification.title")}
                  </span>
                )}
                <Notice />
              </div>
              <div
                style={{
                  ...dividerStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapse ? "center" : "space-between",
                  gap: 8,
                  color: "#fff",
                  padding: collapse ? "12px 0" : "12px 8px",
                  marginBottom: 8,
                }}
              >
                {!collapse && (
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {isDark ? t("theme.dark") : t("theme.light")}
                  </span>
                )}
                <Tooltip title={t("theme.toggle")}>
                  <IconButton
                    size="small"
                    onClick={toggleMode}
                    aria-label={t("theme.toggle")}
                    sx={{ color: "#fff", minWidth: 36, minHeight: 36 }}
                  >
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                  </IconButton>
                </Tooltip>
              </div>
              <div
                style={{
                  ...dividerStyle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapse ? "center" : "space-between",
                  gap: 8,
                  color: "#fff",
                  padding: collapse ? "12px 0" : "12px 8px",
                  marginBottom: 8,
                }}
              >
                {!collapse && (
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {t("sidebar.language.title")}
                  </span>
                )}
                <LanguageSwitcher />
              </div>
              <div style={dividerStyle}>
              <Dropdown placement="top" menu={{ items: menuItems }}>
                {dom}
              </Dropdown>
              </div>
            </div>
          );
        },
      }}
    >
      <PageContainer pageHeaderRender={() => <AppBreadcrumb />}>{children}</PageContainer>
    </ProLayout>
    </BreadcrumbProvider>
  );
};

export default Sidebar;
